import { useEffect, useReducer } from 'react'

function getScript(src: HTMLScriptElement['src']) {
  return document.querySelector<HTMLScriptElement>(`script[src="${src}"]`)
}

async function loadScript(src: HTMLScriptElement['src']) {
  return new Promise((resolve, reject) => {
    let script = getScript(src)
    if (script) {
      resolve(script)
      return
    }

    script = document.createElement('script')
    script.src = src
    script.onload = () => resolve(script)
    script.onerror = reject
    script.async = true

    document.body.appendChild(script)
  })
}

interface ScriptState {
  isLoading: boolean
  hasError: boolean
}

const initialState = {
  isLoading: true,
  hasError: false,
}

type ScriptAction = 'loaded' | 'failed'

function reducer(state: ScriptState, action: ScriptAction) {
  switch (action) {
    case 'loaded':
      return {
        isLoading: false,
        hasError: false,
      }
    case 'failed':
      return {
        isLoading: false,
        hasError: true,
      }
    default:
      return state
  }
}

function useLazyScript(src: HTMLScriptElement['src']) {
  const [{ isLoading, hasError }, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    async function load() {
      try {
        await loadScript(src)
        dispatch('loaded')
      } catch (_) {
        dispatch('failed')
      }
    }

    load()
  }, [src])

  return { isLoading, hasError }
}

export { useLazyScript }
